import { z } from 'zod';
import { employeeContextSchema, isUnknownContext, portalContextSchema } from './access.js';
import { DEFAULT_INTELLIGENCE_LEVEL, intelligenceLevelSchema, moduleAgentSettingSchema, moduleStateSchema, } from './admin.js';
import { DEFAULT_CLAIM_NAMESPACE, readTraceClaims } from './claims.js';
import { MAX_HOSTNAME_LENGTH, normalizeHostname } from './domains.js';
import { isUnknownValue, knownEntries, knownOr } from './forward-compatible.js';
import { productKeySchema } from './products.js';
import { STAFF_ROLES, orgRoleSchema, staffRoleSchema } from './roles.js';
/**
 * platform-api's internal API: service-to-service calls from product and service backends, never from a
 * browser or a person's session. Its routes (every PLATFORM_API entry under INTERNAL_API_PATH_PREFIX) accept
 * only access tokens for the internal audience, issued to an allowlisted service principal through the
 * client-credentials grant (Auth0: azp "<client id>", sub "<client id>@clients"). Customer and staff tokens are
 * refused there, and internal tokens are refused on every other route. This is the default audience; an
 * environment may set its own (INTERNAL_API_AUDIENCE in platform-api).
 */
export const DEFAULT_INTERNAL_API_AUDIENCE = 'urn:trace:api:platform-internal';
export const INTERNAL_API_PATH_PREFIX = '/v1/internal/';
/** True for an internal route path or route pattern: requests to it carry an internal-audience service token. */
export function isInternalApiPath(path) {
    return path === INTERNAL_API_PATH_PREFIX.slice(0, -1) || path.startsWith(INTERNAL_API_PATH_PREFIX);
}
/**
 * Products a product backend asks about: every product a person enters through an employee or a portal
 * context. Trace Admin is not among them (its backend is platform-api's own staff routes).
 */
export const checkableProductKeySchema = productKeySchema.exclude(['admin']);
export const CHECKABLE_PRODUCT_KEYS = checkableProductKeySchema.options;
/** True for a bare hostname exactly as normalizeHostname returns it (lowercase, no scheme or port), or localhost. */
function isNormalizedHostname(value) {
    if (value === 'localhost')
        return true;
    const result = normalizeHostname(value);
    return result.ok && result.hostname === value;
}
/**
 * The host of the page the person is on, as a product backend names it in the access check: a bare, lowercase
 * hostname (no scheme, no port, no IP address), or localhost. accessCheckHostname turns an Origin header value
 * or a host with its port into one. Never the API's own host (see InternalAccessCheckRequestOptions.host).
 */
export const accessCheckHostSchema = z
    .string()
    .max(MAX_HOSTNAME_LENGTH)
    .refine(isNormalizedHostname, 'must be a lowercase hostname without a scheme or a port');
/**
 * The access check host for an origin, such as the Origin header of the frontend's call
 * ("https://app.northwind.com"), or a host with its port ("app.northwind.com", "localhost:4740", what
 * DestinationGate hands checkAccess): its hostname, lowercase and in punycode. Throws a TypeError for anything
 * else (an IP address, a path, credentials, the opaque origin "null"): refuse the request instead of asking
 * without the host.
 */
export function accessCheckHostname(input) {
    let url;
    try {
        url = new URL(input.includes('://') ? input : `http://${input}`);
    }
    catch {
        throw new TypeError('accessCheckHostname: not a host');
    }
    if (url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
        throw new TypeError('accessCheckHostname: a host carries no credentials, path, query or fragment');
    }
    const hostname = url.hostname.toLowerCase();
    if (hostname === 'localhost')
        return hostname;
    const result = normalizeHostname(hostname);
    if (!result.ok)
        throw new TypeError(`accessCheckHostname: not a hostname (${result.reason})`);
    return result.hostname;
}
/**
 * POST /v1/internal/access/check: may this person use this product, in this organization (or this portal
 * and customer account)? A product backend asks it for every session it serves, after verifying the
 * person's product access token itself. The identity and the scope come from that verified token:
 * internalAccessCheckRequestFor builds the request from its payload.
 *
 * - issuer and subject: the token's iss and sub. platform-api answers only for issuers it trusts itself.
 * - organizationId for an employee product (trace_org_id), portalId and customerAccountId for the portal
 *   (portal_id, customer_account_id; organizationId there names the provider and is optional). Without a
 *   scope the answer is never allowed: it is context_required with the contexts to choose from.
 * - connection: the token's connection claim. An organization that enforces SSO accepts only its own, so
 *   leaving it out can only refuse (sso_required), never widen access.
 * - audience (optional; a later version requires it, so send it now): the verified token's aud as given, a
 *   string or an array. platform-api refuses the check (400, audited) unless it is the registry audience of
 *   productKey, or an array that contains it, so a token minted for another product never confirms this one.
 * - host (optional): the host of the page the person is on (accessCheckHostSchema; where a backend takes it
 *   from: InternalAccessCheckRequestOptions.host). Given, platform-api answers as the person sees it on that
 *   host, with the narrowing of GET /v1/me/access and POST /v1/navigation/decide: on a customer host only that
 *   host's tenant counts (another context the person holds is refused with host_mismatch and never named), on
 *   an unknown or disabled host nothing does (the API's own hostname is such a host), and a Trace hostname (the
 *   hub's or a registered application's, localhost in development) turns narrowing off.
 * - issuedAt: the token's iat, in seconds since the epoch. Without it, any session revocation of the person
 *   refuses the check. Any nonnegative number passes here, and platform-api refuses one later than its own
 *   clock plus 60 seconds (400 validation_failed, audited): an issue time in the future, a millisecond value
 *   among them, would pass every revocation. The bound is platform-api's alone so that every such refusal is
 *   audited (a schema refusal would never reach the service).
 *
 * Unknown fields are refused (a misspelled scope must not turn into an unscoped question). Issuer, subject
 * and connection refuse control characters: none appears in an Auth0 value, and a NUL reached Postgres as
 * an invalid byte sequence, which turned a refusal into a 503 and left the attempt unaudited.
 */
const tokenText = (max) => z
    .string()
    .min(1)
    .max(max)
    // eslint-disable-next-line no-control-regex -- the point is to find control characters
    .regex(/^[^\u0000-\u001f\u007f]*$/, 'must not contain control characters');
export const internalAccessCheckRequestSchema = z
    .strictObject({
    issuer: tokenText(320),
    subject: tokenText(255),
    productKey: checkableProductKeySchema,
    organizationId: z.uuid().optional(),
    portalId: z.uuid().optional(),
    customerAccountId: z.uuid().optional(),
    connection: tokenText(320).optional(),
    audience: z.union([tokenText(200), z.array(tokenText(200)).min(1).max(16)]).optional(),
    host: accessCheckHostSchema.optional(),
    /** Seconds since the epoch (a JWT iat), never milliseconds; platform-api bounds it by its clock (above). */
    issuedAt: z.number().nonnegative().optional(),
})
    .superRefine((request, ctx) => {
    if ((request.portalId === undefined) !== (request.customerAccountId === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['portalId'],
            message: 'portalId and customerAccountId come together',
        });
    }
    if (request.productKey !== 'portal' &&
        (request.portalId !== undefined || request.customerAccountId !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['portalId'],
            message: 'a portal scope is for the portal product only',
        });
    }
});
/**
 * Why the check refused. Every reason it shares with navigation/decide is spelled the same way, so a
 * destination can send the person to the hub page that explains it. Its own reasons:
 * - context_required: the request named no organization (or portal and account); `choices` lists the
 *   contexts that could open the product, and the person picks one (the product token then carries it).
 * - product_unavailable: the product is not deployed in this environment.
 * - sso_required: the organization enforces its own sign-in and this session came through another
 *   connection; signing in again through the organization changes the answer.
 * - session_revoked: the person's sessions were revoked after this token was issued.
 * - host_mismatch (only when the request names a host): the host the person is on does not serve that
 *   context (another tenant's, or an unknown or disabled host); the answer then names no context.
 */
export const ACCESS_CHECK_DENY_REASONS = [
    'context_required',
    'membership_required',
    'portal_scope_required',
    'organization_suspended',
    'membership_suspended',
    'product_not_entitled',
    'product_unavailable',
    'sso_required',
    'session_revoked',
    'host_mismatch',
];
export const accessCheckDenyReasonSchema = z.enum(ACCESS_CHECK_DENY_REASONS);
/** An employee or portal context exactly as GET /v1/me/access builds it. Never a staff context. */
export const productContextSchema = z.discriminatedUnion('type', [
    employeeContextSchema,
    portalContextSchema,
]);
/** The person the token belongs to. Email is for display only, never an identity key. */
export const accessCheckUserSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
});
/**
 * The person's grant for the product in that context. productRole is the product's own role: for an
 * employee product the role on the membership's product grant (null when the grant names none), for the
 * portal the portal role (portal_admin or portal_user).
 */
export const productGrantSchema = z.object({
    productKey: productKeySchema,
    productRole: z.string().nullable(),
    /**
     * The product's own reference for this grant, from the invitation that granted it (internalInvitationRequestSchema
     * externalRef), or null. Trace Enterprise uses it to claim the local user row it migrated. Optional, so a client of
     * this version still reads an older server and existing code that builds a grant still compiles: absent means none.
     */
    externalRef: z.string().nullable().optional(),
});
/**
 * The answer of POST /v1/internal/access/check. allowed is true only when the product is available in the
 * requested context: never for sso_required, suspended or not_entitled. A refusal carries no grant; it
 * carries the context when the person holds the one asked about (so sso hints can be used), and
 * `choices` with context_required. maxAgeSeconds bounds how long the product may cache the answer.
 */
export const internalAccessCheckResponseSchema = z.discriminatedUnion('allowed', [
    z.object({
        allowed: z.literal(true),
        user: accessCheckUserSchema,
        context: productContextSchema,
        productGrant: productGrantSchema,
        /**
         * The person's active Trace staff role, or null when they are not staff. Lets a product gate its own
         * platform-staff pages on the platform's staff record (never on an organization-scoped role). Optional (absent
         * from an older server, which a product reads as not staff). 0.22.0: a staff role this copy does not know reads
         * as null (not staff), so it grants nothing and never fails the answer.
         */
        staffRole: knownOr(staffRoleSchema.nullable().optional(), (raw) => isUnknownValue(raw, STAFF_ROLES), null),
        maxAgeSeconds: z.number().int().nonnegative(),
    }),
    z.object({
        allowed: z.literal(false),
        reason: accessCheckDenyReasonSchema,
        user: accessCheckUserSchema.nullable(),
        context: productContextSchema.nullable(),
        productGrant: z.null(),
        /** 0.22.0: a choice this copy cannot read (isUnknownContext) is dropped. */
        choices: knownEntries(productContextSchema, (raw) => isUnknownContext(raw, ['employee', 'portal'])).optional(),
        maxAgeSeconds: z.number().int().nonnegative(),
    }),
]);
/**
 * The internal access check request for a product access token the product backend has ALREADY VERIFIED
 * (signature, issuer, its own audience, expiry): issuer, subject, audience, issue time and connection from the
 * token, the scope from its Trace claims, and the host when the caller gives it. Throws when the token lacks
 * iss, sub or iat, when its aud is neither a string nor a list of strings, when its Trace claims are malformed, when its scope does not fit the product (a portal scope at an
 * employee product), or when the host is not a host: refuse such a token instead of asking. The third
 * argument may still be the claim namespace alone.
 */
export function internalAccessCheckRequestFor(payload, productKey, options = {}) {
    const { namespace = DEFAULT_CLAIM_NAMESPACE, host } = typeof options === 'string' ? { namespace: options, host: undefined } : options;
    const { iss, sub, iat, aud } = payload;
    if (typeof iss !== 'string' || typeof sub !== 'string' || typeof iat !== 'number') {
        throw new TypeError('internalAccessCheckRequestFor: the token must carry iss, sub and iat');
    }
    if (aud !== undefined &&
        typeof aud !== 'string' &&
        !(Array.isArray(aud) && aud.every((entry) => typeof entry === 'string'))) {
        throw new TypeError('internalAccessCheckRequestFor: the token aud must be a string or a list of strings');
    }
    const claims = readTraceClaims(payload, namespace);
    if (!claims.success) {
        throw new TypeError('internalAccessCheckRequestFor: the token carries malformed Trace claims', {
            cause: claims.error,
        });
    }
    const scope = claims.data;
    return internalAccessCheckRequestSchema.parse({
        issuer: iss,
        subject: sub,
        productKey,
        ...(scope.organizationId !== undefined ? { organizationId: scope.organizationId } : {}),
        ...(scope.portalId !== undefined
            ? { portalId: scope.portalId, customerAccountId: scope.customerAccountId }
            : {}),
        ...(scope.connection !== undefined ? { connection: scope.connection } : {}),
        ...(aud !== undefined ? { audience: aud } : {}),
        ...(host !== undefined ? { host: accessCheckHostname(host) } : {}),
        issuedAt: iat,
    });
}
/* ------------------------------------------------------------------------------------------------ */
/* Invitations from a product backend (POST /v1/internal/invitations)                               */
/* ------------------------------------------------------------------------------------------------ */
/**
 * Organization roles a product backend may hand out: never org_owner (owners are made by people). Trace Enterprise
 * invites its administrators as org_admin and billing_admin, everyone else as member.
 */
export const INTERNAL_INVITATION_ORG_ROLES = ['org_admin', 'billing_admin', 'member'];
export const internalInvitationOrgRoleSchema = orgRoleSchema.extract(INTERNAL_INVITATION_ORG_ROLES);
/** An opaque reference of the product's own (letters, digits and : . _ -; at most 200 characters). */
export const externalRefSchema = z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9:._-]{0,199}$/, 'letters, digits and : . _ - only, at most 200 characters');
/**
 * An invitation a product backend sends to someone it already serves, so they join the product's linked platform
 * organization with access to that product only. The principal must be allowed to invite for productKey
 * (platform-api INVITATION_SERVICE_PRINCIPALS) and the organization must subscribe to the product. Idempotent per
 * organization, address and product: a pending invitation is not sent again, and a person whose grant already
 * carries this externalRef is not invited again.
 */
export const internalInvitationRequestSchema = z
    .object({
    organizationId: z.uuid(),
    email: z.email().max(254),
    productKey: checkableProductKeySchema.exclude(['portal']),
    roles: z.array(internalInvitationOrgRoleSchema).min(1).max(INTERNAL_INVITATION_ORG_ROLES.length),
    productRole: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,63}$/, 'a product role: lowercase letters, digits and underscores')
        .nullable(),
    externalRef: externalRefSchema,
    expiresInDays: z.number().int().min(1).max(30).default(14),
})
    .strict();
export const INTERNAL_INVITATION_OUTCOMES = ['sent', 'already_pending', 'already_linked'];
export const internalInvitationResponseSchema = z.object({
    outcome: z.enum(INTERNAL_INVITATION_OUTCOMES),
    /** The pending invitation (sent or already pending); null when the person is already linked. */
    invitationId: z.uuid().nullable(),
    expiresAt: z.iso.datetime().nullable(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Organization settings for product backends (GET /v1/internal/organizations/{orgId}/settings)      */
/* ------------------------------------------------------------------------------------------------ */
/**
 * 0.19.0 (self-serve sign-up, spec 2026-10-18 piece 2): the product the settings read is about. Unknown fields are
 * refused.
 */
export const internalOrganizationSettingsQuerySchema = z.strictObject({
    productKey: checkableProductKeySchema,
});
const internalModuleSettingFields = {
    moduleKey: z.string(),
    state: moduleStateSchema,
    on: z.boolean(),
    intelligenceLevel: intelligenceLevelSchema.nullable(),
};
/**
 * The agent an answer from a platform-api before 0.24.0 reads as: off, at shadow (the narrowest mode), at the level
 * the same answer gives the module (DEFAULT_INTELLIGENCE_LEVEL when it gives none), so modules[].agent.intelligenceLevel
 * and modules[].intelligenceLevel never disagree.
 */
function olderPlatformAgentSetting(moduleLevel) {
    return {
        enabled: false,
        highestMode: 'shadow',
        intelligenceLevel: moduleLevel ?? DEFAULT_INTELLIGENCE_LEVEL,
    };
}
/**
 * One module of the product as the organization has it now: its effective state (the same computation as the action
 * authorization), whether it takes new work (on: the state is enabled or connected) and its intelligence level (the
 * configured one; the default for a module that carries a level and was never configured; null for a module without
 * levels, which no module of catalog 2026-10-22.2 is, since every module carries the agent setting).
 *
 * 0.24.0: agent, the module's agent setting (moduleAgentSettingSchema): on or off, the highest mode it may work in and
 * its intelligence level (the same value as intelligenceLevel above), as configured, with the catalog's defaults where
 * nothing is configured. The agent works only while the module is on. An answer without it (a platform-api before
 * 0.24.0) reads as an agent that is off, at the narrowest mode, so an older platform never turns an agent on, and at
 * the module's own intelligenceLevel (DEFAULT_INTELLIGENCE_LEVEL when that is null), so the two levels always agree.
 */
// A codec, not a .default() or a transform: the fallback agent depends on a sibling field (intelligenceLevel), which
// a field default cannot see, and platform-api serializes this answer with z.encode, which refuses transforms (see
// forward-compatible.ts). The input keeps the module's own fields typed; only agent may be absent there.
const internalModuleSettingAnswerSchema = z.object({
    ...internalModuleSettingFields,
    agent: moduleAgentSettingSchema.optional(),
});
export const internalModuleSettingSchema = z.codec(internalModuleSettingAnswerSchema, z.object({ ...internalModuleSettingFields, agent: moduleAgentSettingSchema }), {
    decode: (raw) => ({ ...raw, agent: raw.agent ?? olderPlatformAgentSetting(raw.intelligenceLevel) }),
    // Encoding (platform-api's own serializer) has already checked the strict output, agent included, so the cast
    // only restates that highestMode is a known mode (knownOr types its encoded side as unknown).
    encode: (value) => ({ ...value }),
});
/**
 * The answer of the settings read: the organization's intelligence level for the product (the configured level of the
 * product's first module that carries one, in catalog order; DEFAULT_INTELLIGENCE_LEVEL when none is configured), every
 * module of the product released to the organization, and disabledModules: the keys of those whose configuration turns
 * them off (enabled false). Read at checkedAt and never cached by platform-api; product backends may cache it briefly.
 * Side effects still ask POST /v1/internal/actions/authorize, which applies the same switches.
 */
export const internalOrganizationSettingsSchema = z.object({
    organizationId: z.uuid(),
    productKey: checkableProductKeySchema,
    intelligenceLevel: intelligenceLevelSchema,
    modules: z.array(internalModuleSettingSchema),
    disabledModules: z.array(z.string()),
    checkedAt: z.iso.datetime(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Member directory for product backends (GET /v1/internal/organizations/{orgId}/members), 0.25.0    */
/* ------------------------------------------------------------------------------------------------ */
/**
 * Membership statuses as the member directory reports them: the membership's own status, except that a member whose
 * platform account is disabled reads as suspended (nobody can sign in as them).
 */
export const INTERNAL_MEMBER_STATUSES = ['invited', 'active', 'suspended', 'removed'];
export const internalMemberStatusSchema = z.enum(INTERNAL_MEMBER_STATUSES);
/** Products that read their organizations' member directory: the employee products (the portal has no members). */
export const memberDirectoryProductKeySchema = checkableProductKeySchema.exclude(['portal']);
/**
 * 0.25.0 (Brokers ledger N-5): the query of the member directory read. productKey is the calling product, which must be
 * the one platform-api maps the calling principal to (MEMBER_DIRECTORY_SERVICE_PRINCIPALS). Optional lookups, never
 * both at once: email (matched case-insensitively against the member's primary email, exactly, never as a search) or
 * userId (the platform user id the access check answers as user.id). status filters by membership status; without it
 * removed members are left out, so they appear only when status=removed is asked for (for matching imported people to
 * people who left). Keyset pages of at most 200 (default 100); cursor is the nextCursor of the previous page, opaque
 * and at most a few hundred characters whatever the names hold. Unknown fields are refused, so a misspelled filter is a
 * 400 instead of a wider answer.
 */
export const internalMemberDirectoryQuerySchema = z
    .strictObject({
    productKey: memberDirectoryProductKeySchema,
    email: z.email().max(254).optional(),
    userId: z.uuid().optional(),
    status: internalMemberStatusSchema.optional(),
    cursor: z.string().min(1).max(500).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
})
    .superRefine((query, ctx) => {
    if (query.email !== undefined && query.userId !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['email'],
            message: 'look a member up by email or by userId, not both',
        });
    }
});
/**
 * One member as a product backend sees it, and nothing more: no roles, no product grants, no sign-in identities, no
 * phone, no multi-factor state. userId is the platform user id (the access check's user.id, the token's trace_user_id).
 * email is the primary email, for display and for matching imported people, never an identity key; null when the
 * platform holds none. A status this copy does not know reads as suspended, which grants nothing.
 */
export const internalDirectoryMemberSchema = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    status: knownOr(internalMemberStatusSchema, (raw) => isUnknownValue(raw, INTERNAL_MEMBER_STATUSES), 'suspended'),
});
/**
 * The answer of the member directory read: members of the organization in the path who hold access to the product
 * (removed ones only when status=removed was asked for), ordered by display name (its first 64 characters, then user
 * id), with the cursor of the next page or null on the last one. Read at this
 * moment and never cached by platform-api (cache-control no-store); every read is audited.
 */
export const internalMemberDirectorySchema = z.object({
    organizationId: z.uuid(),
    productKey: memberDirectoryProductKeySchema,
    items: z.array(internalDirectoryMemberSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=internal.js.map